import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {format} from 'date-fns'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {openAlert} from '../../../../../../_metronic/elements/NotificationAlert'
import {useErrorHandler} from '../../../../../../_metronic/helpers/ErrorHandler'
import {sortByLabel} from '../../../../../shared/optionSort'
import {clearStableRequest, getOrCreateStableRequest} from '../../../services/stableRequestId'
import * as ReportService from '../../services/ReportService'
import {useEconomicReportContext} from '../EconomicReportContext'
import {
  ECONOMIC_CROP_IDS,
  defaultEconomicMoistureTreatment,
  nonGrainMoisturePayload,
  usesGrainInputs,
} from './economicCropPolicy'
import {
  getOriginalMoistureAssessment,
  getSelectedYieldCorrectionAssessment,
  INPUT_YIELD_BASES,
  isValidMoisturePercent,
} from './moistureTreatment'
import {
  changeDeclaredHarvestedSurfaceHa,
  changeDeclaredSownSurfaceHa,
  changeHarvestStatus,
  changeSowingStatus,
  changeYieldMapCoverageStatus,
  clearYieldMapCoverageStatus,
  emptySurfaceReconciliation,
  getPrescriptionSurfaceReference,
  getSurfaceReconciliationAssessment,
} from './surfaceReconciliation'

const CreateEconomicReportContext = createContext()

export function useCreateEconomicReportContext() {
  return useContext(CreateEconomicReportContext)
}

const parseDecimal = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const raw = String(value).trim()
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw
  const numericValue = Number(normalized)

  return Number.isFinite(numericValue) ? numericValue : null
}

const fieldLooksNumeric = (field) => {
  const type = String(field?.type || '').toLowerCase()
  const samples = Array.isArray(field?.sampleValues)
    ? field.sampleValues
    : Array.isArray(field?.sample_values)
    ? field.sample_values
    : []

  return (
    /(float|double|decimal|numeric|number|int|real)/i.test(type) ||
    samples.some((value) => parseDecimal(value) !== null)
  )
}

const findPreferredField = (fields = [], patterns = []) => {
  const byName = fields.find((field) => {
    const name = String(field?.name || '').toLowerCase()
    return patterns.some((pattern) => pattern.test(name))
  })

  return byName || fields[0] || null
}

const ENVIRONMENT_PRODUCTIVITY_RANK = {
  'MUY ALTA PRODUCTIVIDAD': 0,
  MAP: 0,
  'ALTA PRODUCTIVIDAD': 1,
  AP: 1,
  'POTENCIAL PRODUCTIVO': 2,
  PP: 2,
  'MEDIA PRODUCTIVIDAD': 3,
  MP: 3,
  'BAJA PRODUCTIVIDAD': 4,
  BP: 4,
  'MUY BAJA PRODUCTIVIDAD': 5,
  MBP: 5,
  GR: 6,
}

const normalizeEnvironmentValue = (value) =>
  String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()

const getEnvironmentProductivityRank = (value) =>
  ENVIRONMENT_PRODUCTIVITY_RANK[normalizeEnvironmentValue(value)] ?? Number.MAX_SAFE_INTEGER

const sortEnvironmentValues = (values = []) =>
  [...values].sort((a, b) => {
    const productivityDiff = getEnvironmentProductivityRank(a) - getEnvironmentProductivityRank(b)

    if (productivityDiff !== 0) {
      return productivityDiff
    }

    return String(a).localeCompare(String(b), 'es', {numeric: true, sensitivity: 'base'})
  })

const uniqueEnvironmentValues = (field) => {
  const samples = Array.isArray(field?.sampleValues)
    ? field.sampleValues
    : Array.isArray(field?.sample_values)
    ? field.sample_values
    : []
  const uniqueValues = samples
    .filter((value) => value !== null && value !== undefined && String(value).trim().length > 0)
    .map((value) => String(value).trim())
    .filter((value, index, values) => values.indexOf(value) === index)

  return sortEnvironmentValues(uniqueValues)
}

const getPrescriptionDefinition = (inspection) =>
  inspection?.prescriptionDefinition || inspection?.prescription_definition || {}

const getUniquePrescriptionFieldNames = (fieldNames = []) => {
  const usedFieldNames = new Set()

  return fieldNames.map((fieldName) => {
    const normalizedFieldName = String(fieldName || '').trim()

    if (!normalizedFieldName || usedFieldNames.has(normalizedFieldName)) {
      return ''
    }

    usedFieldNames.add(normalizedFieldName)
    return normalizedFieldName
  })
}

const getLandSearchLabel = ({field, land}) => {
  const fieldName = field?.name || ''
  const landName = land?.name || land?.label || ''

  if (fieldName && landName.toLowerCase().startsWith(`${fieldName.toLowerCase()} -`)) {
    return landName
  }

  return fieldName ? `${fieldName} - ${landName}` : landName
}

const inferFieldNameFromLandName = (landName) => {
  const [fieldName] = String(landName || '').split(' - ')
  return fieldName?.trim() || ''
}

const getFeatureProperties = (feature) => feature?.properties || {}

const getFieldId = (field = {}) => field.field_id || field.fieldId || field.id || ''

const getLandId = (land = {}) => land.land_id || land.landId || land.id || ''

const normalizeEconomicLandText = (value) =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\blote\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const getCanonicalLandName = (fieldName, landName) => {
  const normalizedField = normalizeEconomicLandText(fieldName)
  const normalizedLand = normalizeEconomicLandText(landName)

  if (normalizedField && normalizedLand.startsWith(`${normalizedField} -`)) {
    return normalizedLand.slice(normalizedField.length + 2).trim()
  }

  return normalizedLand
}

const getLandMatchKey = ({field, land}) => {
  const fieldKey = normalizeEconomicLandText(field?.name || getFieldId(field))
  const landKey = getCanonicalLandName(field?.name, land?.name || land?.label || getLandId(land))
  const surface = parseDecimal(land?.ha)
  const surfaceKey = surface === null ? '' : surface.toFixed(2)

  return `${fieldKey}|${landKey}|${surfaceKey}`
}

const buildEconomicLandOptions = (geoJson) => {
  const features = Array.isArray(geoJson?.features) ? geoJson.features : []
  const fieldsById = new Map()

  features.forEach((feature) => {
    const properties = getFeatureProperties(feature)

    if (properties.resource_type !== 'field') {
      return
    }

    const fieldId = properties.field_id || properties.fieldId

    if (fieldId) {
      fieldsById.set(fieldId, properties)
    }
  })

  return sortByLabel(
    features
      .map(getFeatureProperties)
      .filter((properties) => properties.resource_type === 'land')
      .map((land) => {
        const fieldId = land.field_id || land.fieldId || ''
        const field = fieldsById.get(fieldId) || {}
        const fieldName = field.name || inferFieldNameFromLandName(land.name) || fieldId
        const landId = getLandId(land)

        return {
          field: {
            ...field,
            field_id: fieldId,
            id: fieldId,
            name: fieldName,
          },
          land: {
            ...land,
            id: landId,
            land_id: landId,
            name: land.name || land.label || landId,
          },
        }
      })
      .filter(({field, land}) => !!field.field_id && !!land.land_id),
    getLandSearchLabel
  )
}

const SHAPEFILE_UPLOAD_KEYS = new Set(['shp', 'shx', 'dbf', 'prj', 'cpg'])
const FIRST_ECONOMIC_REPORT_CAMPAIGN_YEAR = 2020
const CROP_OPTIONS = ECONOMIC_CROP_IDS

const getCampaignLabel = (startYear) => `${startYear}/${String(startYear + 1).slice(-2)}`

const buildCampaignOptions = (date = new Date()) => {
  const currentStartYear = Math.max(FIRST_ECONOMIC_REPORT_CAMPAIGN_YEAR, date.getFullYear())

  return Array.from(
    {length: currentStartYear - FIRST_ECONOMIC_REPORT_CAMPAIGN_YEAR + 1},
    (_, index) => getCampaignLabel(currentStartYear - index)
  )
}

const CAMPAIGN_OPTIONS = buildCampaignOptions()
const DEFAULT_CAMPAIGN = CAMPAIGN_OPTIONS[0] || getCampaignLabel(2020)

const YIELD_UNIT_OPTIONS = ['ton_per_ha', 'kg_per_ha', 'qq_per_ha']
const SEED_UNIT_OPTIONS = ['seeds_per_ha', 'thousand_seeds_per_ha', 'bags_per_ha', 'kg_per_ha']
const FERTILIZER_UNIT_OPTIONS = ['kg_per_ha', 'ton_per_ha', 'l_per_ha']
const FIXED_COST_KEYS = [
  'sowing',
  'spraying',
  'herbicides',
  'harvest',
  'rent',
  'agronomist',
  'administration',
]

const emptyFixedCosts = () =>
  [...FIXED_COST_KEYS, 'planting_material'].reduce((accumulator, key) => {
    accumulator[key] = ''
    return accumulator
  }, {})

const emptyYieldCorrection = () => ({
  minYieldTonsHa: '',
  maxYieldTonsHa: '',
  totalHarvestedTons: '',
})

const emptyMoistureTreatment = defaultEconomicMoistureTreatment

const getPrescriptionSummary = (inspection) =>
  inspection?.prescriptionSummary || inspection?.prescription_summary || {}

const getSummaryEnvironmentRows = (inspection) => {
  const summary = getPrescriptionSummary(inspection)
  const rows = Array.isArray(summary?.environments) ? summary.environments : []

  return [...rows].sort((a, b) => {
    const environmentA = a.environmentName || a.environmentId || ''
    const environmentB = b.environmentName || b.environmentId || ''
    const productivityDiff =
      getEnvironmentProductivityRank(environmentA) - getEnvironmentProductivityRank(environmentB)

    if (productivityDiff !== 0) {
      return productivityDiff
    }

    return String(environmentA).localeCompare(String(environmentB), 'es', {
      numeric: true,
      sensitivity: 'base',
    })
  })
}

const calculateSeedCost = ({dose, unit, seedsPerBag, bagPrice}) => {
  if (dose === null || bagPrice === null) {
    return null
  }

  if (unit === 'bags_per_ha') {
    return dose * bagPrice
  }

  if (seedsPerBag === null || seedsPerBag <= 0) {
    return null
  }

  if (unit === 'seeds_per_ha') {
    return (dose / seedsPerBag) * bagPrice
  }

  if (unit === 'thousand_seeds_per_ha') {
    return ((dose * 1000) / seedsPerBag) * bagPrice
  }

  return null
}

const calculateFertilizerCost = ({dose, unit, price}) => {
  if (dose === null || price === null) {
    return null
  }

  if (unit === 'kg_per_ha') {
    return (dose / 1000) * price
  }

  if (unit === 'ton_per_ha' || unit === 'l_per_ha') {
    return dose * price
  }

  return null
}

const sumParsedValues = (values) =>
  values.reduce((total, value) => total + (parseDecimal(value) ?? 0), 0)

const buildSeedPricingPayload = ({seedBagPrice, seedSeedsPerBag}) => ({
  mode: 'usd_per_bag',
  price: parseDecimal(seedBagPrice),
  bagPriceUsd: parseDecimal(seedBagPrice),
  seedsPerBag: parseDecimal(seedSeedsPerBag),
})

const buildFertilizerPricingPayload = ({price, unit}) => {
  if (!price) {
    return null
  }

  return {
    unit: unit === 'l_per_ha' ? 'usd_per_liter' : 'usd_per_ton',
    price: parseDecimal(price),
    priceUsdPerLiter: unit === 'l_per_ha' ? parseDecimal(price) : null,
    priceUsdPerTon: unit === 'l_per_ha' ? null : parseDecimal(price),
  }
}

const appendEconomicUploadFiles = (formData, files) => {
  files.forEach((file) => {
    const extension = String(file?.name || '')
      .split('.')
      .pop()
      .toLowerCase()

    if (extension === 'zip') {
      formData.append('file', file)
      return
    }

    if (SHAPEFILE_UPLOAD_KEYS.has(extension)) {
      formData.append(extension, file)
    }
  })
}

const reportLocaleFromLanguage = (language) =>
  String(language || 'es')
    .toLowerCase()
    .startsWith('en')
    ? 'en-US'
    : 'es-AR'

export function CreateEconomicReportProvider({children}) {
  const {t, i18n} = useTranslation()
  const navigate = useNavigate()
  const {errorHandler} = useErrorHandler()
  const {load} = useEconomicReportContext()
  const [activeStep, setActiveStep] = useState('general')
  const [name, setName] = useState(() =>
    t('reports:economic.create.inputs.name_default', {date: format(new Date(), 'dd/LL/yyyy')})
  )
  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN)
  const [crop, setCrop] = useState('corn')
  const grainInputs = usesGrainInputs(crop)
  const fixedCostKeys = useMemo(
    () => (grainInputs ? FIXED_COST_KEYS : [...FIXED_COST_KEYS, 'planting_material']),
    [grainInputs]
  )
  const [selectedLandId, setSelectedLandId] = useState('')
  const [prescriptionFiles, setPrescriptionFiles] = useState([])
  const [yieldFiles, setYieldFiles] = useState([])
  const [prescriptionInspection, setPrescriptionInspection] = useState(null)
  const [yieldInspection, setYieldInspection] = useState(null)
  const [environmentFieldName, setEnvironmentFieldName] = useState('')
  const [seedFieldName, setSeedFieldName] = useState('')
  const [fertilizer1FieldName, setFertilizer1FieldName] = useState('')
  const [fertilizer2FieldName, setFertilizer2FieldName] = useState('')
  const [yieldFieldName, setYieldFieldName] = useState('')
  const [yieldUnit, setYieldUnit] = useState('ton_per_ha')
  const [cleanYieldMap, setCleanYieldMap] = useState(false)
  const [calibrateYieldMap, setCalibrateYieldMap] = useState(false)
  const [yieldCorrection, setYieldCorrection] = useState(emptyYieldCorrection)
  const [moistureTreatment, setMoistureTreatment] = useState(() => emptyMoistureTreatment('corn'))
  const [surfaceReconciliation, setSurfaceReconciliation] = useState(() =>
    emptySurfaceReconciliation()
  )
  const [netPrice, setNetPrice] = useState('')
  const [seedUnit, setSeedUnit] = useState('seeds_per_ha')
  const [seedSeedsPerBag, setSeedSeedsPerBag] = useState('')
  const [seedBagPrice, setSeedBagPrice] = useState('')
  const [fertilizer1Unit, setFertilizer1Unit] = useState('kg_per_ha')
  const [fertilizer1Price, setFertilizer1Price] = useState('')
  const [fertilizer2Unit, setFertilizer2Unit] = useState('kg_per_ha')
  const [fertilizer2Price, setFertilizer2Price] = useState('')
  const [fixedCosts, setFixedCosts] = useState(() => emptyFixedCosts())
  const [fixedCostTotalInput, setFixedCostTotalInput] = useState('')
  const [cleanYieldConfirmed, setCleanYieldConfirmed] = useState(false)
  const [calibrationConfirmed, setCalibrationConfirmed] = useState(false)
  const [rawYieldConfirmed, setRawYieldConfirmed] = useState(false)
  const [originalMoistureConfirmed, setOriginalMoistureConfirmed] = useState(false)
  const [calibrationSourceConfirmed, setCalibrationSourceConfirmed] = useState(false)
  const [inspecting, setInspecting] = useState(null)
  const [loading, setLoading] = useState(false)
  const [landOptions, setLandOptions] = useState([])
  const inspectionRevision = useRef(0)

  const resetYieldCorrection = useCallback(() => {
    setYieldCorrection(emptyYieldCorrection())
  }, [])

  const resetMoistureTreatment = useCallback(
    (nextCrop = crop) => {
      setMoistureTreatment(emptyMoistureTreatment(nextCrop))
    },
    [crop]
  )

  const resetSurfaceReconciliation = useCallback(() => {
    setSurfaceReconciliation(emptySurfaceReconciliation())
  }, [])

  const setSowingStatus = (nextStatus) => {
    setSurfaceReconciliation((current) => changeSowingStatus(current, nextStatus))
  }

  const setDeclaredSownSurfaceHa = (nextSurfaceHa) => {
    setSurfaceReconciliation((current) => changeDeclaredSownSurfaceHa(current, nextSurfaceHa))
  }

  const setHarvestStatus = (nextStatus) => {
    setSurfaceReconciliation((current) => changeHarvestStatus(current, nextStatus))
  }

  const setDeclaredHarvestedSurfaceHa = (nextSurfaceHa) => {
    setSurfaceReconciliation((current) => changeDeclaredHarvestedSurfaceHa(current, nextSurfaceHa))
  }

  const setYieldMapCoverageStatus = (nextStatus) => {
    setSurfaceReconciliation((current) => changeYieldMapCoverageStatus(current, nextStatus))
  }

  const changeCrop = (nextCrop) => {
    inspectionRevision.current += 1
    setInspecting(null)
    setPrescriptionInspection(null)
    setCrop(nextCrop)
    setYieldFieldName('')
    setCleanYieldMap(false)
    setCalibrateYieldMap(false)
    setCleanYieldConfirmed(false)
    setCalibrationConfirmed(false)
    setRawYieldConfirmed(false)
    setOriginalMoistureConfirmed(false)
    setCalibrationSourceConfirmed(false)
    resetYieldCorrection()
    resetMoistureTreatment(nextCrop)
    resetSurfaceReconciliation()
    if (!usesGrainInputs(nextCrop)) setSeedFieldName('')
  }

  const changeYieldFieldName = (nextFieldName) => {
    setYieldFieldName(nextFieldName)
    setRawYieldConfirmed(false)
    setOriginalMoistureConfirmed(false)
    setCalibrationSourceConfirmed(false)
    setMoistureTreatment((current) => ({
      ...current,
      originalMoistureField: '',
    }))
  }

  const changeOriginalMoistureField = (nextFieldName) => {
    setOriginalMoistureConfirmed(false)
    setCalibrationSourceConfirmed(false)
    setMoistureTreatment((current) => ({
      ...current,
      originalMoistureField: nextFieldName,
    }))
  }

  const loadEconomicLandOptions = async () => {
    const res = await errorHandler(ReportService.listEconomicReportGeoJson())

    if (!res?.ok) {
      return null
    }

    return buildEconomicLandOptions(res.data)
  }

  useEffect(() => {
    let isMounted = true

    loadEconomicLandOptions().then((options) => {
      if (!isMounted || !options) {
        return
      }

      setLandOptions(options)
    })

    return () => {
      isMounted = false
    }
    // Load economic-report lands once from the same backend that creates the report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedLand = useMemo(() => {
    return landOptions.find(({land}) => getLandId(land) === selectedLandId) || null
  }, [landOptions, selectedLandId])

  const prescriptionSurfaceReference = useMemo(
    () => getPrescriptionSurfaceReference(prescriptionInspection),
    [prescriptionInspection]
  )

  useEffect(() => {
    if (selectedLandId && landOptions.length > 0 && !selectedLand) {
      changeSelectedLandId('')
    }
    // Keep selections tied to the economic-report backend list, not stale map/search IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landOptions.length, selectedLand, selectedLandId])

  const environmentField = useMemo(() => {
    return (
      (prescriptionInspection?.fields || []).find((field) => field.name === environmentFieldName) ||
      null
    )
  }, [prescriptionInspection, environmentFieldName])

  const detectedPrescriptionDefinition = useMemo(
    () => getPrescriptionDefinition(prescriptionInspection),
    [prescriptionInspection]
  )
  const prescriptionFieldOptions = useMemo(
    () => sortByLabel(prescriptionInspection?.fields || [], (field) => field.name),
    [prescriptionInspection]
  )
  const prescriptionDoseFieldOptions = useMemo(() => {
    const numericFields = prescriptionFieldOptions.filter(fieldLooksNumeric)
    return numericFields.length > 0 ? numericFields : prescriptionFieldOptions
  }, [prescriptionFieldOptions])
  const prescriptionDefinition = useMemo(() => {
    const environmentDefinition = detectedPrescriptionDefinition?.environmentDefinition || {}
    const seed = detectedPrescriptionDefinition?.seed || {}
    const fertilizer1 = detectedPrescriptionDefinition?.fertilizer1 || {}
    const fertilizer2 = detectedPrescriptionDefinition?.fertilizer2 || {}

    return {
      ...detectedPrescriptionDefinition,
      environmentDefinition: {
        ...environmentDefinition,
        field: environmentFieldName,
      },
      seed: {
        ...seed,
        field: grainInputs ? seedFieldName : null,
        ...(grainInputs ? {} : {enabled: false}),
      },
      fertilizer1: {
        ...fertilizer1,
        enabled: Boolean(fertilizer1FieldName),
        field: fertilizer1FieldName || null,
      },
      fertilizer2: {
        ...fertilizer2,
        enabled: Boolean(fertilizer2FieldName),
        field: fertilizer2FieldName || null,
      },
    }
  }, [
    detectedPrescriptionDefinition,
    grainInputs,
    environmentFieldName,
    fertilizer1FieldName,
    fertilizer2FieldName,
    seedFieldName,
  ])
  const prescriptionSummaryRows = useMemo(
    () => getSummaryEnvironmentRows(prescriptionInspection),
    [prescriptionInspection]
  )
  const environments = useMemo(() => {
    if (prescriptionSummaryRows.length > 0) {
      return prescriptionSummaryRows.map(
        (environment) => environment.environmentName || environment.environmentId
      )
    }

    return uniqueEnvironmentValues(environmentField)
  }, [environmentField, prescriptionSummaryRows])

  const yieldFieldOptions = useMemo(() => {
    const fields = yieldInspection?.fields || []
    const numericFields = fields.filter(fieldLooksNumeric)
    return sortByLabel(numericFields.length ? numericFields : fields, (field) => field.name)
  }, [yieldInspection])

  const detectedFertilizer1 = prescriptionDefinition?.fertilizer1
  const detectedFertilizer2 = prescriptionDefinition?.fertilizer2
  const fertilizer1Enabled = Boolean(detectedFertilizer1?.enabled && detectedFertilizer1?.field)
  const fertilizer2Enabled = Boolean(detectedFertilizer2?.enabled && detectedFertilizer2?.field)
  const usesDetectedPrescriptionFields = useMemo(
    () =>
      environmentFieldName ===
        (detectedPrescriptionDefinition?.environmentDefinition?.field || '') &&
      (!grainInputs || seedFieldName === (detectedPrescriptionDefinition?.seed?.field || '')) &&
      fertilizer1FieldName === (detectedPrescriptionDefinition?.fertilizer1?.field || '') &&
      fertilizer2FieldName === (detectedPrescriptionDefinition?.fertilizer2?.field || ''),
    [
      detectedPrescriptionDefinition,
      grainInputs,
      environmentFieldName,
      fertilizer1FieldName,
      fertilizer2FieldName,
      seedFieldName,
    ]
  )

  useEffect(() => {
    if (grainInputs && detectedPrescriptionDefinition?.seed?.unit) {
      setSeedUnit(detectedPrescriptionDefinition.seed.unit)
    }
    if (detectedPrescriptionDefinition?.fertilizer1?.unit) {
      setFertilizer1Unit(detectedPrescriptionDefinition.fertilizer1.unit)
    }
    if (detectedPrescriptionDefinition?.fertilizer2?.unit) {
      setFertilizer2Unit(detectedPrescriptionDefinition.fertilizer2.unit)
    }
  }, [detectedPrescriptionDefinition, grainInputs])

  const hasItemizedFixedCosts = useMemo(
    () => fixedCostKeys.some((key) => parseDecimal(fixedCosts[key]) !== null),
    [fixedCostKeys, fixedCosts]
  )
  const itemizedFixedCostTotal = useMemo(
    () => sumParsedValues(fixedCostKeys.map((key) => fixedCosts[key])),
    [fixedCostKeys, fixedCosts]
  )
  const parsedFixedCostTotalInput = parseDecimal(fixedCostTotalInput)
  const fixedCostTotal = parsedFixedCostTotalInput ?? itemizedFixedCostTotal
  const fixedCostTotalValue =
    fixedCostTotalInput !== ''
      ? fixedCostTotalInput
      : hasItemizedFixedCosts
      ? itemizedFixedCostTotal
      : ''

  const environmentCostPreview = useMemo(() => {
    const seedPrice = parseDecimal(seedBagPrice)
    const seedsPerBag = parseDecimal(seedSeedsPerBag)
    const fertilizer1ParsedPrice = parseDecimal(fertilizer1Price)
    const fertilizer2ParsedPrice = parseDecimal(fertilizer2Price)

    return prescriptionSummaryRows.map((environment) => {
      const seedDose = grainInputs ? parseDecimal(environment.seedDose) : null
      const fertilizer1Dose = parseDecimal(environment.fertilizer1Dose)
      const fertilizer2Dose = parseDecimal(environment.fertilizer2Dose)
      const seedCost = grainInputs
        ? calculateSeedCost({
            dose: seedDose,
            unit: seedUnit,
            seedsPerBag,
            bagPrice: seedPrice,
          })
        : 0
      const fertilizer1Cost = fertilizer1Enabled
        ? calculateFertilizerCost({
            dose: fertilizer1Dose,
            unit: fertilizer1Unit,
            price: fertilizer1ParsedPrice,
          })
        : 0
      const fertilizer2Cost = fertilizer2Enabled
        ? calculateFertilizerCost({
            dose: fertilizer2Dose,
            unit: fertilizer2Unit,
            price: fertilizer2ParsedPrice,
          })
        : 0
      const variableCost =
        seedCost === null || fertilizer1Cost === null || fertilizer2Cost === null
          ? null
          : seedCost + fertilizer1Cost + fertilizer2Cost
      const totalCost = variableCost === null ? null : variableCost + fixedCostTotal

      return {
        ...environment,
        seedDose,
        fertilizer1Dose,
        fertilizer2Dose,
        seedCostUsdHa: seedCost,
        fertilizer1CostUsdHa: fertilizer1Cost,
        fertilizer2CostUsdHa: fertilizer2Cost,
        variableCostUsdHa: variableCost,
        fixedCostUsdHa: fixedCostTotal,
        totalCostUsdHa: totalCost,
        totalCostUsd:
          totalCost === null || parseDecimal(environment.surfaceHa) === null
            ? null
            : totalCost * parseDecimal(environment.surfaceHa),
      }
    })
  }, [
    fertilizer1Enabled,
    fertilizer1Price,
    fertilizer1Unit,
    fertilizer2Enabled,
    fertilizer2Price,
    fertilizer2Unit,
    fixedCostTotal,
    grainInputs,
    prescriptionSummaryRows,
    seedBagPrice,
    seedSeedsPerBag,
    seedUnit,
  ])

  const validationState = useMemo(() => {
    const parsedPrice = parseDecimal(netPrice)
    const parsedItemizedFixedCosts = fixedCostKeys.reduce((accumulator, key) => {
      const parsedValue = parseDecimal(fixedCosts[key])

      if (parsedValue !== null) {
        accumulator[key] = parsedValue
      }

      return accumulator
    }, {})
    const parsedFixedCostUsdPerHa = parseDecimal(fixedCostTotalInput)
    const parsedFixedCosts = parsedFixedCostUsdPerHa === null ? parsedItemizedFixedCosts : null
    const parsedYieldCorrection = {
      minYieldTonsHa: parseDecimal(yieldCorrection.minYieldTonsHa),
      maxYieldTonsHa: parseDecimal(yieldCorrection.maxYieldTonsHa),
      totalHarvestedTons: parseDecimal(yieldCorrection.totalHarvestedTons),
    }
    const parsedOutputCommercialTargetPercent = parseDecimal(
      moistureTreatment.outputCommercialTargetPercent
    )
    const parsedMoistureTreatment = {
      inputCorrectionTargetPercent: null,
      outputCommercialTargetPercent: parsedOutputCommercialTargetPercent,
    }
    const surfaceReconciliationAssessment = getSurfaceReconciliationAssessment(
      surfaceReconciliation,
      prescriptionSurfaceReference.kind === 'inside_land'
        ? prescriptionSurfaceReference.surfaceHa
        : null
    )

    const hasGeneralData = !!name.trim() && !!campaign.trim()
    const hasLand = !!selectedLand
    const hasPrescription =
      !!(prescriptionInspection?.uploadId || prescriptionInspection?.upload_id) &&
      prescriptionInspection?.valid === true
    const hasYieldMap =
      !!(yieldInspection?.uploadId || yieldInspection?.upload_id) && yieldInspection?.valid === true
    const selectedPrescriptionFieldNames = [
      environmentFieldName,
      grainInputs ? seedFieldName : '',
      fertilizer1FieldName,
      fertilizer2FieldName,
    ].filter(Boolean)
    const hasUniquePrescriptionFields =
      new Set(selectedPrescriptionFieldNames).size === selectedPrescriptionFieldNames.length
    const hasDetectedPrescription =
      hasPrescription &&
      !!environmentFieldName &&
      (!grainInputs || !!seedFieldName) &&
      hasUniquePrescriptionFields &&
      environments.length > 0
    const hasYieldField = hasYieldMap && !!yieldFieldName && !!yieldUnit
    const hasSeedPricing =
      !grainInputs ||
      (parseDecimal(seedBagPrice) !== null &&
        (seedUnit === 'bags_per_ha' || parseDecimal(seedSeedsPerBag) !== null))
    const hasFertilizer1Pricing = !fertilizer1Enabled || parseDecimal(fertilizer1Price) !== null
    const hasFertilizer2Pricing = !fertilizer2Enabled || parseDecimal(fertilizer2Price) !== null
    const hasFixedCosts =
      parsedFixedCostUsdPerHa !== null || Object.keys(parsedItemizedFixedCosts).length > 0
    const hasCosts =
      environmentCostPreview.length > 0 &&
      (!usesDetectedPrescriptionFields ||
        environmentCostPreview.every((environment) => environment.totalCostUsdHa !== null))
    const hasEconomics =
      parsedPrice !== null &&
      hasSeedPricing &&
      hasFertilizer1Pricing &&
      hasFertilizer2Pricing &&
      hasFixedCosts &&
      hasCosts
    const hasCleaningValues =
      parsedYieldCorrection.minYieldTonsHa !== null || parsedYieldCorrection.maxYieldTonsHa !== null
    const hasValidCleaning =
      !cleanYieldMap ||
      (hasCleaningValues &&
        (parsedYieldCorrection.minYieldTonsHa === null ||
          parsedYieldCorrection.maxYieldTonsHa === null ||
          parsedYieldCorrection.minYieldTonsHa < parsedYieldCorrection.maxYieldTonsHa))
    const hasValidCalibrationValues =
      !calibrateYieldMap ||
      (parsedYieldCorrection.totalHarvestedTons !== null &&
        parsedYieldCorrection.totalHarvestedTons > 0)
    const hasCalibrationSourceConfirmation = !calibrateYieldMap || calibrationSourceConfirmed
    const hasValidCalibration = hasValidCalibrationValues && hasCalibrationSourceConfirmation
    const hasCleaningDecision = (cleanYieldMap && hasValidCleaning) || cleanYieldConfirmed
    const hasCalibrationDecision = calibrateYieldMap ? hasValidCalibration : calibrationConfirmed
    const hasCleaningAndCalibration = hasCleaningDecision && hasCalibrationDecision
    const hasMoistureBasis =
      !grainInputs || INPUT_YIELD_BASES.includes(moistureTreatment.inputYieldBasis)
    const rawMoistureAssessment = grainInputs
      ? getOriginalMoistureAssessment(
          yieldInspection,
          moistureTreatment.originalMoistureField,
          yieldFieldName
        )
      : {valid: true, reason: 'not_applicable'}
    const selectedYieldCorrectionAssessment = getSelectedYieldCorrectionAssessment(
      yieldInspection,
      yieldFieldName,
      moistureTreatment.originalMoistureField
    )
    const hasOriginalMoistureField = !grainInputs || !!moistureTreatment.originalMoistureField
    const hasValidOutputMoisture =
      !grainInputs || isValidMoisturePercent(parsedMoistureTreatment.outputCommercialTargetPercent)
    const hasValidMoistureTreatment =
      hasMoistureBasis &&
      rawMoistureAssessment.valid &&
      selectedYieldCorrectionAssessment.valid &&
      hasValidOutputMoisture &&
      rawYieldConfirmed &&
      (!grainInputs || originalMoistureConfirmed)
    const hasYieldSetup =
      hasYieldField &&
      selectedYieldCorrectionAssessment.valid &&
      rawYieldConfirmed &&
      hasOriginalMoistureField &&
      rawMoistureAssessment.valid &&
      (!grainInputs || originalMoistureConfirmed)
    const hasConfirmations = hasCleaningAndCalibration && hasValidMoistureTreatment
    const hasSurfaceReconciliation = surfaceReconciliationAssessment.valid

    return {
      hasConfirmations,
      hasCalibrationDecision,
      hasCleaningDecision,
      hasCleaningAndCalibration,
      hasCosts,
      hasDetectedPrescription,
      hasEconomics,
      hasGeneralData,
      hasLand,
      hasMoistureBasis,
      hasOriginalMoistureField,
      hasPrescription,
      hasSurfaceReconciliation,
      hasValidCalibration,
      hasValidCalibrationValues,
      hasCalibrationSourceConfirmation,
      hasValidCleaning,
      hasValidMoistureTreatment,
      hasValidOriginalMoistureField: rawMoistureAssessment.valid,
      hasValidOutputMoisture,
      hasYieldSetup,
      hasYieldField,
      hasYieldMap,
      isValid:
        hasGeneralData &&
        hasLand &&
        hasDetectedPrescription &&
        hasYieldField &&
        hasEconomics &&
        hasConfirmations &&
        hasSurfaceReconciliation,
      parsedFixedCosts,
      parsedFixedCostUsdPerHa,
      parsedPrice,
      parsedMoistureTreatment,
      parsedYieldCorrection,
      rawMoistureAssessment,
      selectedYieldCorrectionAssessment,
      surfaceReconciliationAssessment,
    }
  }, [
    campaign,
    calibrateYieldMap,
    calibrationSourceConfirmed,
    calibrationConfirmed,
    cleanYieldMap,
    cleanYieldConfirmed,
    environmentCostPreview,
    environmentFieldName,
    environments.length,
    fertilizer1Enabled,
    fertilizer1FieldName,
    fertilizer1Price,
    fertilizer2Enabled,
    fertilizer2FieldName,
    fertilizer2Price,
    fixedCosts,
    fixedCostKeys,
    grainInputs,
    fixedCostTotalInput,
    name,
    netPrice,
    moistureTreatment,
    prescriptionInspection,
    prescriptionSurfaceReference,
    rawYieldConfirmed,
    seedBagPrice,
    seedFieldName,
    seedSeedsPerBag,
    seedUnit,
    selectedLand,
    surfaceReconciliation,
    originalMoistureConfirmed,
    usesDetectedPrescriptionFields,
    yieldCorrection,
    yieldFieldName,
    yieldInspection,
    yieldUnit,
  ])

  const setFilesForKind = (kind, files) => {
    inspectionRevision.current += 1
    setInspecting(null)
    const nextFiles = Array.from(files || [])

    if (kind === 'prescription') {
      setPrescriptionFiles(nextFiles)
      setPrescriptionInspection(null)
      setEnvironmentFieldName('')
      setSeedFieldName('')
      setFertilizer1FieldName('')
      setFertilizer2FieldName('')
      setSeedSeedsPerBag('')
      setSeedBagPrice('')
      setFertilizer1Price('')
      setFertilizer2Price('')
      resetSurfaceReconciliation()
      return
    }

    setYieldFiles(nextFiles)
    setYieldInspection(null)
    setYieldFieldName('')
    setCleanYieldMap(false)
    setCalibrateYieldMap(false)
    setCleanYieldConfirmed(false)
    setCalibrationConfirmed(false)
    setRawYieldConfirmed(false)
    setOriginalMoistureConfirmed(false)
    setCalibrationSourceConfirmed(false)
    resetYieldCorrection()
    resetMoistureTreatment()
    setSurfaceReconciliation((current) => clearYieldMapCoverageStatus(current))
  }

  const clearFilesForKind = (kind) => {
    setFilesForKind(kind, [])
  }

  const removeFileForKind = (kind, fileIndex) => {
    const currentFiles = kind === 'prescription' ? prescriptionFiles : yieldFiles
    setFilesForKind(
      kind,
      currentFiles.filter((_, index) => index !== fileIndex)
    )
  }

  const changeSelectedLandId = (landId) => {
    inspectionRevision.current += 1
    setInspecting(null)
    setSelectedLandId(landId)
    setPrescriptionInspection(null)
    setYieldInspection(null)
    setEnvironmentFieldName('')
    setSeedFieldName('')
    setFertilizer1FieldName('')
    setFertilizer2FieldName('')
    setYieldFieldName('')
    setSeedSeedsPerBag('')
    setSeedBagPrice('')
    setFertilizer1Price('')
    setFertilizer2Price('')
    setFixedCosts(emptyFixedCosts())
    setFixedCostTotalInput('')
    setCleanYieldMap(false)
    setCalibrateYieldMap(false)
    setCleanYieldConfirmed(false)
    setCalibrationConfirmed(false)
    setRawYieldConfirmed(false)
    setOriginalMoistureConfirmed(false)
    setCalibrationSourceConfirmed(false)
    resetYieldCorrection()
    resetMoistureTreatment()
    resetSurfaceReconciliation()
  }

  const resolveSubmitLand = async () => {
    if (!selectedLand) {
      return null
    }

    const refreshedOptions = await loadEconomicLandOptions()

    if (!refreshedOptions) {
      return selectedLand
    }

    setLandOptions(refreshedOptions)

    const currentLandId = getLandId(selectedLand.land)
    const byId = refreshedOptions.find(({land}) => getLandId(land) === currentLandId)

    if (byId) {
      return byId
    }

    const currentMatchKey = getLandMatchKey(selectedLand)
    const byStableLabel = refreshedOptions.find(
      (option) => getLandMatchKey(option) === currentMatchKey
    )

    if (byStableLabel) {
      setSelectedLandId(getLandId(byStableLabel.land))
      return byStableLabel
    }

    setSelectedLandId('')
    openAlert(
      'danger',
      t('reports:economic.create.errors.title'),
      t('reports:economic.create.errors.land_unavailable')
    )

    return null
  }

  const inspectUpload = useCallback(
    async (kind) => {
      const files = kind === 'prescription' ? prescriptionFiles : yieldFiles

      if (!selectedLand || files.length < 1) {
        openAlert(
          'danger',
          t('reports:economic.create.errors.title'),
          t('reports:economic.create.errors.required')
        )
        return
      }

      setInspecting(kind)
      const currentInspectionRevision = ++inspectionRevision.current

      const formData = new FormData()
      formData.append('kind', kind === 'prescription' ? 'prescription' : 'yield_map')
      formData.append('landId', selectedLand.land.land_id || selectedLand.land.id)
      formData.append('crop', crop)
      appendEconomicUploadFiles(formData, files)

      const res = await errorHandler(
        ReportService.inspectEconomicReportUpload(formData),
        [400, 413]
      )
      // Crop, file and land changes invalidate the request scope, including late responses.
      if (currentInspectionRevision !== inspectionRevision.current) return
      if (!res.ok && res.error_type === 'payload_too_large') {
        const inspection = {
          valid: false,
          errors: [t('reports:economic.create.errors.upload_too_large')],
        }

        if (kind === 'prescription') {
          setPrescriptionInspection(inspection)
        } else {
          setYieldInspection(inspection)
        }

        openAlert(
          'danger',
          t('reports:economic.create.errors.title'),
          t('reports:economic.create.errors.upload_too_large')
        )
        setInspecting(null)
        return
      }

      if (!res.ok && res.error_type !== 'field_error') {
        const inspection = {
          valid: false,
          errors: [t('reports:economic.create.errors.upload')],
        }

        if (kind === 'prescription') {
          setPrescriptionInspection(inspection)
        } else {
          setYieldInspection(inspection)
        }

        setInspecting(null)
        return
      }

      const inspection = res.data

      if (kind === 'prescription') {
        setPrescriptionInspection(inspection || null)
        if (inspection?.valid) {
          const nextDetectedPrescriptionDefinition = getPrescriptionDefinition(inspection)
          const fields = inspection.fields || []
          const doseFields = fields.filter(fieldLooksNumeric)
          const detectedEnvironmentField =
            nextDetectedPrescriptionDefinition?.environmentDefinition?.field
          const preferred = findPreferredField(fields, [
            /^amb$/i,
            /amb/i,
            /ambient/i,
            /ambiente/i,
            /environment/i,
            /class/i,
            /clase/i,
            /zona/i,
            /zone/i,
          ])
          const detectedSeedField = grainInputs
            ? nextDetectedPrescriptionDefinition?.seed?.field ||
              findPreferredField(doseFields, [/sem/i, /seed/i, /dens/i, /dose/i])?.name ||
              ''
            : ''
          const [nextEnvironmentField, nextSeedField, nextFertilizer1Field, nextFertilizer2Field] =
            getUniquePrescriptionFieldNames([
              detectedEnvironmentField || preferred?.name || '',
              detectedSeedField,
              nextDetectedPrescriptionDefinition?.fertilizer1?.field,
              nextDetectedPrescriptionDefinition?.fertilizer2?.field,
            ])

          setEnvironmentFieldName(nextEnvironmentField)
          setSeedFieldName(nextSeedField)
          setFertilizer1FieldName(nextFertilizer1Field)
          setFertilizer2FieldName(nextFertilizer2Field)
        }
      } else {
        setYieldInspection(inspection || null)
        if (inspection?.valid) {
          setYieldFieldName('')
        }
      }

      if (!inspection?.valid) {
        openAlert(
          'danger',
          t('reports:economic.create.errors.title'),
          inspection?.errors?.[0] || t('reports:economic.create.errors.invalid_upload')
        )
      }

      setInspecting(null)
    },
    [crop, errorHandler, grainInputs, prescriptionFiles, selectedLand, t, yieldFiles]
  )

  useEffect(() => {
    if (!selectedLand || inspecting !== null) {
      return
    }

    if (prescriptionFiles.length > 0 && prescriptionInspection === null) {
      inspectUpload('prescription')
      return
    }

    if (yieldFiles.length > 0 && yieldInspection === null) {
      inspectUpload('yield_map')
    }
  }, [
    inspectUpload,
    inspecting,
    prescriptionFiles.length,
    prescriptionInspection,
    selectedLand,
    yieldFiles.length,
    yieldInspection,
  ])

  const validate = () => {
    if (!validationState.hasGeneralData || !validationState.hasLand) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.required')
      )
      return null
    }

    if (!validationState.hasPrescription || !validationState.hasYieldMap) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.invalid_upload')
      )
      return null
    }

    if (!validationState.hasDetectedPrescription || !validationState.hasYieldField) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.required')
      )
      return null
    }

    if (!validationState.hasSurfaceReconciliation) {
      const reconciliationErrors = Object.values(
        validationState.surfaceReconciliationAssessment.errors
      )
      const errorKey = reconciliationErrors.includes('harvest_exceeds_sown')
        ? 'harvest_exceeds_sown'
        : reconciliationErrors.includes('positive_surface')
        ? 'positive_surface'
        : 'required'

      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t(`reports:economic.create.surface_reconciliation.${
          !grainInputs && errorKey === 'harvest_exceeds_sown' ? 'non_grain' : 'errors'
        }.${errorKey}`, {
          value: validationState.surfaceReconciliationAssessment.resolvedSownSurfaceHa ?? '-',
        })
      )
      return null
    }

    if (!validationState.selectedYieldCorrectionAssessment.valid) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.corrected_yield_field', {
          field: validationState.selectedYieldCorrectionAssessment.suggestedField || '-',
        })
      )
      return null
    }

    if (!validationState.hasMoistureBasis) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.moisture_basis')
      )
      return null
    }

    if (
      !validationState.hasOriginalMoistureField ||
      !validationState.hasValidOriginalMoistureField
    ) {
      const moistureReason = validationState.rawMoistureAssessment.reason
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t(
          moistureReason === 'uniform_corrected'
            ? 'reports:economic.create.errors.uniform_moisture_field'
            : moistureReason === 'corrected_at_target'
            ? 'reports:economic.create.errors.corrected_moisture_field'
            : 'reports:economic.create.errors.moisture_field',
          {
            value: validationState.rawMoistureAssessment.targetPercent,
          }
        )
      )
      return null
    }

    if (!validationState.hasValidOutputMoisture) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.moisture_target')
      )
      return null
    }

    if (!validationState.hasEconomics) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        validationState.hasCosts
          ? t('reports:economic.create.errors.required')
          : t('reports:economic.create.errors.costs')
      )
      return null
    }

    if (!validationState.hasConfirmations) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t(
          calibrateYieldMap && !validationState.hasCalibrationSourceConfirmation
            ? 'reports:economic.create.errors.calibration_source_confirmation'
            : 'reports:economic.create.errors.raw_input_confirmations'
        )
      )
      return null
    }

    if (!validationState.isValid) {
      openAlert(
        'danger',
        t('reports:economic.create.errors.title'),
        t('reports:economic.create.errors.required')
      )
      return null
    }

    return {
      parsedFixedCosts: validationState.parsedFixedCosts,
      parsedFixedCostUsdPerHa: validationState.parsedFixedCostUsdPerHa,
      parsedMoistureTreatment: validationState.parsedMoistureTreatment,
      parsedPrice: validationState.parsedPrice,
      surfaceReconciliation: validationState.surfaceReconciliationAssessment.payload,
      parsedYieldCorrection: validationState.parsedYieldCorrection,
    }
  }

  const onSubmit = async () => {
    const validated = validate()

    if (!validated) {
      return
    }

    setLoading(true)

    const submitLand = await resolveSubmitLand()

    if (!submitLand) {
      setLoading(false)
      return
    }

    const seedPricing = grainInputs
      ? buildSeedPricingPayload({seedBagPrice, seedSeedsPerBag})
      : null
    const fertilizer1Pricing = fertilizer1Enabled
      ? buildFertilizerPricingPayload({price: fertilizer1Price, unit: fertilizer1Unit})
      : null
    const fertilizer2Pricing = fertilizer2Enabled
      ? buildFertilizerPricingPayload({price: fertilizer2Price, unit: fertilizer2Unit})
      : null
    const environmentCosts = usesDetectedPrescriptionFields
      ? environmentCostPreview.reduce((accumulator, environment) => {
          const key = environment.environmentId || environment.environmentName
          accumulator[key] = {
            seedDose: environment.seedDose,
            fertilizer1Dose: environment.fertilizer1Dose,
            fertilizer2Dose: environment.fertilizer2Dose,
            seedCostUsdPerHa: environment.seedCostUsdHa,
            fertilizer1CostUsdPerHa: environment.fertilizer1CostUsdHa,
            fertilizer2CostUsdPerHa: environment.fertilizer2CostUsdHa,
            variableCostUsdPerHa: environment.variableCostUsdHa,
            fixedCostUsdPerHa: environment.fixedCostUsdHa,
          }
          return accumulator
        }, {})
      : null
    const correctionPayload =
      cleanYieldMap || calibrateYieldMap
        ? {
            enabled: true,
            minYieldTonsHa: cleanYieldMap ? validated.parsedYieldCorrection.minYieldTonsHa : null,
            maxYieldTonsHa: cleanYieldMap ? validated.parsedYieldCorrection.maxYieldTonsHa : null,
            totalHarvestedTons: calibrateYieldMap
              ? validated.parsedYieldCorrection.totalHarvestedTons
              : null,
            totalHarvestedTonsBasis: grainInputs ? 'harvested_moisture' : 'harvested_mass',
            totalHarvestedTonsConfirmed: calibrateYieldMap ? calibrationSourceConfirmed : null,
          }
        : {enabled: false}
    const moistureTreatmentPayload = grainInputs
      ? {
          inputYieldBasis: moistureTreatment.inputYieldBasis,
          originalMoistureField: moistureTreatment.originalMoistureField,
          inputCorrectionTargetPercent: null,
          outputCommercialTargetPercent:
            validated.parsedMoistureTreatment.outputCommercialTargetPercent,
          rawYieldConfirmed,
          originalMoistureConfirmed,
        }
      : nonGrainMoisturePayload(rawYieldConfirmed)

    const data = {
      name: name.trim(),
      fieldId: getFieldId(submitLand.field),
      landId: getLandId(submitLand.land),
      campaign: campaign.trim(),
      crop,
      currency: 'USD',
      locale: reportLocaleFromLanguage(i18n.resolvedLanguage || i18n.language),
      prescription: {
        uploadId: prescriptionInspection.uploadId || prescriptionInspection.upload_id,
        fileNames: prescriptionFiles.map((file) => file.name).filter(Boolean),
        environmentDefinition: {
          field: environmentFieldName,
        },
        seed: {
          ...prescriptionDefinition.seed,
          field: grainInputs ? seedFieldName : null,
          unit: grainInputs ? seedUnit : null,
          ...(grainInputs ? {} : {enabled: false}),
        },
        fertilizer1: {
          ...prescriptionDefinition.fertilizer1,
          enabled: Boolean(fertilizer1FieldName),
          field: fertilizer1FieldName || null,
          unit: fertilizer1Unit,
        },
        fertilizer2: {
          ...prescriptionDefinition.fertilizer2,
          enabled: Boolean(fertilizer2FieldName),
          field: fertilizer2FieldName || null,
          unit: fertilizer2Unit,
        },
      },
      yieldMap: {
        uploadId: yieldInspection.uploadId || yieldInspection.upload_id,
        fileNames: yieldFiles.map((file) => file.name).filter(Boolean),
        field: yieldFieldName,
        unit: yieldUnit,
        correction: correctionPayload,
        moistureTreatment: moistureTreatmentPayload,
        cleanAndCalibratedConfirmed:
          validationState.hasCleaningDecision && validationState.hasCalibrationDecision,
      },
      economics: {
        netCropPriceUsdPerTon: validated.parsedPrice,
        allowPolygonsWithoutYield: true,
        reportContext: {
          fieldName: submitLand.field?.name || null,
          landName: submitLand.land?.name || submitLand.land?.label || null,
          landSurfaceHa: parseDecimal(submitLand.land?.ha),
        },
        seedPricing,
        fertilizer1Pricing,
        fertilizer2Pricing,
        fixedCosts: validated.parsedFixedCosts,
        fixedCostUsdPerHa: fixedCostTotal,
        surfaceReconciliation: validated.surfaceReconciliation,
        ...(environmentCosts ? {environmentCosts} : {}),
      },
    }
    const reportRequest = getOrCreateStableRequest('specific:economic-report-request', data)
    data.requestId = reportRequest.requestId

    errorHandler(ReportService.newEconomicReport(data)).then((res) => {
      if (!res.ok) {
        setLoading(false)
        return
      }

      clearStableRequest(reportRequest.storageKey)
      openAlert(
        'info',
        t('reports:economic.create.success.title'),
        t('reports:economic.create.success.message', {name})
      )
      navigate('/gis/reports/economic')
      load()
    })
  }

  const value = {
    activeStep,
    calibrateYieldMap,
    calibrationSourceConfirmed,
    calibrationConfirmed,
    campaign,
    campaignOptions: CAMPAIGN_OPTIONS,
    cleanYieldMap,
    cleanYieldConfirmed,
    crop,
    cropOptions: CROP_OPTIONS,
    detectedFertilizer1,
    detectedFertilizer2,
    environmentCostPreview,
    environmentFieldName,
    environments,
    fertilizer1FieldName,
    fertilizer1Enabled,
    fertilizer1Price,
    fertilizer1Unit,
    fertilizer2FieldName,
    fertilizer2Enabled,
    fertilizer2Price,
    fertilizer2Unit,
    fixedCostKeys,
    grainInputs,
    fixedCostTotal,
    fixedCostTotalInput,
    fixedCostTotalValue,
    fixedCosts,
    inspecting,
    isFormComplete: validationState.isValid,
    landOptions,
    loading,
    moistureTreatment,
    name,
    netPrice,
    prescriptionFiles,
    prescriptionDefinition,
    prescriptionDoseFieldOptions,
    prescriptionFieldOptions,
    prescriptionInspection,
    prescriptionSurfaceReference,
    prescriptionSummaryRows,
    rawMoistureAssessment: validationState.rawMoistureAssessment,
    rawYieldConfirmed,
    selectedLand,
    selectedLandId,
    selectedYieldCorrectionAssessment: validationState.selectedYieldCorrectionAssessment,
    seedBagPrice,
    seedFieldName,
    seedSeedsPerBag,
    seedUnit,
    seedUnitOptions: SEED_UNIT_OPTIONS,
    surfaceReconciliation,
    surfaceReconciliationAssessment: validationState.surfaceReconciliationAssessment,
    originalMoistureConfirmed,
    stepCompletion: {
      confirmations: validationState.hasConfirmations,
      costs: validationState.hasEconomics,
      general: validationState.hasGeneralData,
      land: validationState.hasLand,
      prescription: validationState.hasDetectedPrescription,
      review: validationState.isValid,
      yield: validationState.hasYieldSetup,
      surfaces: validationState.hasSurfaceReconciliation,
      corrections: validationState.hasConfirmations,
    },
    yieldCorrection,
    yieldFieldName,
    yieldFieldOptions,
    yieldFiles,
    yieldInspection,
    yieldUnit,
    yieldUnitOptions: YIELD_UNIT_OPTIONS,
    fertilizerUnitOptions: FERTILIZER_UNIT_OPTIONS,
    clearFilesForKind,
    changeOriginalMoistureField,
    changeYieldFieldName,
    inspectUpload,
    onSubmit,
    removeFileForKind,
    setCampaign,
    setCalibrateYieldMap,
    setCalibrationSourceConfirmed,
    setCalibrationConfirmed,
    setCleanYieldMap,
    setCleanYieldConfirmed,
    setCrop: changeCrop,
    setEnvironmentFieldName,
    setDeclaredHarvestedSurfaceHa,
    setDeclaredSownSurfaceHa,
    setFertilizer1FieldName,
    setFertilizer1Price,
    setFertilizer1Unit,
    setFertilizer2FieldName,
    setFertilizer2Price,
    setFertilizer2Unit,
    setFilesForKind,
    setFixedCostTotalInput,
    setFixedCosts,
    setName,
    setNetPrice,
    setMoistureTreatment,
    setOriginalMoistureConfirmed,
    setRawYieldConfirmed,
    setHarvestStatus,
    setSelectedLandId: changeSelectedLandId,
    setSeedBagPrice,
    setSeedFieldName,
    setSeedSeedsPerBag,
    setSeedUnit,
    setYieldCorrection,
    setYieldMapCoverageStatus,
    setSowingStatus,
    setYieldFieldName: changeYieldFieldName,
    setYieldUnit,
    setActiveStep,
    resetMoistureTreatment,
    resetYieldCorrection,
  }

  return (
    <CreateEconomicReportContext.Provider value={value}>
      {children}
    </CreateEconomicReportContext.Provider>
  )
}
